/** @jsxImportSource @emotion/react */
import React, { Suspense, useState } from 'react';
import { jsx, css } from '@emotion/react';
import { Link } from 'react-router-dom';
import TablePage from '../../components/page/TablePage';
import PageHeader from '@atlaskit/page-header';
import { FormattedMessage, useIntl } from 'components/i18n';
import RecurringInvoicesTable from './components/RecurringInvoicesTable';
import Button, { ButtonGroup } from '@atlaskit/button';
import { queryCache, usePaginatedQuery, useQuery } from 'react-query';
import api from '../../libs/api';
import { RecurringSale } from './types';
import useApiQuery from '../../hooks/useApiQuery';
import { fetchInvoices } from './SalesPage';
import TextField from '../../components/textfield';
import StatusFilter from './components/StatusFilter';
import { DatePicker } from '../../components/date-picker';
import Spinner from '@atlaskit/spinner';
import PurchasesTable from '../purchase/components/PurchasesTable';
import SearchBar from '../../components/SearchBar';

type Props = {}

const RecurringInvoicesHeader = ({bottomBar}: any) => {
  const actions = <ButtonGroup>
    <Link to="new">
      <Button appearance="primary" onClick={() => null}>
        <FormattedMessage
          id="sale.RecurringInvoices.create"
          defaultMessage="Create recurring invoice"
        />
      </Button>
    </Link>
  </ButtonGroup>;

  return <PageHeader actions={actions} bottomBar={bottomBar}>
    <FormattedMessage id="sale.RecurringInvoices.title" defaultMessage="Recurring invoices" />
  </PageHeader>;
};

const fetchRecurringSales = async () => api.get('/recurring-sales');

const RecurringInvoicesPage: React.FC<Props> = (props) => {
  // const { data, isLoading } = useQuery(['recurringSales'], fetchRecurringSales);
  // const recurringSales: RecurringSale[] = data;
  const intl = useIntl();

  let { queryParams, debouncedUpdateQueryParams, updateQueryParams, handleChangeQueryParam } = useApiQuery({defaultSortBy: 'created DESC'});
  const { page } = queryParams;
  const {search: searchQ, status} = queryParams;
  console.log('status',status);
  const params = {...queryParams, isActive:  status && status.includes('active')}
  const [search, setSearch] = useState<string>(searchQ);
  console.log('params',params);
  const {
    resolvedData,
    latestData,
    isFetching,
    isLoading,
  } = usePaginatedQuery(
    ['recurring-sales', params],
    fetchInvoices
  );

  // Prefetch the next page!
  React.useEffect(() => {
    if (page) {
      queryCache.prefetchQuery(
        ['recurring-sales', {...params, page: params.page + 1}],
        fetchInvoices
      );
    }
  }, [resolvedData]);

  const handleSearch = React.useCallback(
    (value) => {
      setSearch(value)
      debouncedUpdateQueryParams({ search: value, page: 1});
    },
    [debouncedUpdateQueryParams]
  );

  const barContent = React.useMemo(() => {
    const searchLabel = intl.formatMessage({
      id: 'sale.RecurringInvoicesPage.search',
      defaultMessage: 'Search',
      description: 'Search placeholder',
    });

    const itemsContainer = css`
      display: flex;
      align-items: center;
      margin-top: 1rem;
      margin-bottom: 1rem;
      && > div:not(:first-of-type) {
        margin-left: 8px;
      }
    `;

    return (
      <div>
        <div css={itemsContainer}>
          <div css={css`flex: 0 0 400px;`}>
            <SearchBar
              placeholder={searchLabel}
              value={search}
              onChange={handleSearch}
              onReset={() => handleSearch('')}
            />
          </div>
          <div css={css`flex: 0 0 100px;`}>
            <StatusFilter value={status} onChange={handleChangeQueryParam('status')} options={['active', 'inactive']} />
          </div>
          <div>{isFetching && <Spinner/>}</div>
        </div>
      </div>
    );
  }, [intl, status, isFetching, search]);


  return (
    <TablePage header={<RecurringInvoicesHeader bottomBar={barContent} />}>
        <RecurringInvoicesTable data={latestData?.data}
                                pageCount={latestData?.meta?.totalPageCount}
                                updateQueryParams={updateQueryParams}
                                isLoading={isLoading}
                                {...queryParams} />
    </TablePage>
  );
};

export default RecurringInvoicesPage;
